import { ChatActions } from './ChatActions';
import { Dashboard } from './Dashboard';
import { Settings } from './Settings';
import { ladder } from './Ladder';

import Cookies from 'browser-cookies';

import * as connectionActions from 'react/redux/actions/connection';
import store from 'react/redux/store/store';
import Globals from './Globals';
import { ChatRoom } from 'models/ChatRoom.jsx';
import io from 'socket.io-client';
import ConnectionStatus from "./contstants/ConnectionStatus";
import siteSocketChannels from 'session/siteSocketChannels';
import createChannelClaims from 'session/socketChannelClaims';

// The site's one socket connection (docs/site-session.md). session/siteSession
// connects it on every page for a logged-in user; everything else shares it.

function handshakeQuery({ channels, presence }) {
	return { channels: channels.join(','), presence: presence ? '1' : '0' };
}

function setFromQuery(query) {
	return { channels: query.channels ? query.channels.split(',') : [], presence: query.presence === '1' };
}

class SocketConnection {
	constructor() {
		this.connection = null;
		this.newDisconnection = false;
		this.reloadUserlistOnceAuthenticated = false;
		this.socketIoInitiated = false;

		// The set the server has accepted, and a counter so only the answer to
		// the latest setChannels counts.
		this.acceptedChannels = null;
		this.channelRequest = 0;
		// The set the latest handshake sent. A claim made after that handshake
		// left but before 'connect' only changed the query, so 'connect' sends it.
		this.handshakeChannels = null;
		this.channelClaims = createChannelClaims((next) => this.applyChannels(next));

		this.connect = this.connect.bind(this);
		this.setStatus = this.setStatus.bind(this);
		this.send = this.send.bind(this);
		this.connectToSocketIo = this.connectToSocketIo.bind(this);
	}

	setStatus(status) {
		store.dispatch(connectionActions.changeWebsocketStatus(status));
	}

	disconnect() {
		this.socketIoInitiated = false;
		this.socketIoConnection.disconnect();
	}

	// Adds channels (and presence, if asked) to what the socket listens to until
	// the returned release is called. Claims combine with each other and the
	// page's default. Connects first if nothing has yet.
	claimChannels({ channels = [], presence = false } = {}) {
		const release = this.channelClaims.claim({ channels, presence });
		if(!this.socketIoInitiated) {
			this.connect();
		}
		return release;
	}

	// Changes the live socket's channels without reconnecting. The handshake
	// query always holds the wanted set so a reconnect carries it; a refused
	// change puts it back to what the server accepted.
	applyChannels(set) {
		if(!this.socketIoInitiated || !this.socketIoConnection) {
			return;
		}
		const socket = this.socketIoConnection;
		Object.assign(socket.io.opts.query, handshakeQuery(set));
		if(!socket.connected) {
			return;
		}
		const request = ++this.channelRequest;
		socket.emit('setChannels', { channels: set.channels, presence: set.presence }, (reply) => {
			if(request !== this.channelRequest) {
				return;
			}
			if(reply && reply.ok) {
				this.acceptedChannels = { channels: reply.channels, presence: !!reply.presence };
			}
			else {
				console.error(`[WS] setChannels refused: ${reply && reply.error ? reply.error : 'no reply'}`);
			}
			if(this.acceptedChannels) {
				Object.assign(socket.io.opts.query, handshakeQuery(this.acceptedChannels));
			}
		});
	}

	connect(connectionData = {}) {
		const defaults = connectionData.channels
			? { channels: connectionData.channels, presence: !!connectionData.presence }
			: siteSocketChannels();
		this.channelClaims.setDefault(defaults);

		return this.connectToSocketIo(this.channelClaims.current());
	}

	connectToSocketIo(connectData) {
		if(!this.socketIoInitiated) {
			console.warn('Socket Server Connection Initializing');
			this.socketIoInitiated = true;
			this.acceptedChannels = { channels: [...connectData.channels], presence: !!connectData.presence };
			this.handshakeChannels = this.acceptedChannels;
			this.socketIoConnection = io.connect(socketServerUrl, {
				// timeout: 10000,                  //before connect_error and connect_timeout are emitted.
				transports: ['polling', 'websocket'],
				query: {
					id: myUser.id,
					...handshakeQuery(connectData),
				},
				// Read on every (re)connect, so a ticket the site reissues is picked up.
				auth: (callback) => callback({ ticket: Cookies.get('lad_sock_socket_ticket') }),
			});

			this.socketIoConnection.on('connect', () => {
				console.log('[WS] Connected — authenticating...');
				this.setStatus(ConnectionStatus.AUTHENTICATING);
				// The server took the handshake's set; an answer still on its way
				// belongs to the old connection.
				this.channelRequest++;
				this.acceptedChannels = this.handshakeChannels;
				const wanted = this.channelClaims.current();
				if(handshakeQuery(wanted).channels !== handshakeQuery(this.acceptedChannels).channels || wanted.presence !== this.acceptedChannels.presence) {
					this.applyChannels(wanted);
				}
				if(this.newDisconnection) {
					this.newDisconnection = false;
					this.reloadUserlistOnceAuthenticated = true;
					Globals.getUserGoing();
				}
			});
			this.socketIoConnection.on('positionInLine', (data) => {
				console.log(`[WS] Position in queue: ${data.batch} / ${data.size}`);
			});
			this.socketIoConnection.on('authenticated', () => {
				this.setStatus(ConnectionStatus.AUTHENTICATED_AND_OPEN);
				console.log('[WS] Authenticated');
				ChatActions.chatFocus(); //Send visible userlist status to the socket server
				if(this.reloadUserlistOnceAuthenticated) {
					// Userlist changes sent while disconnected are lost.
					this.reloadUserlistOnceAuthenticated = false;
					ChatRoom.reloadUserlistsAfterReconnect();
				}
			});
			this.socketIoConnection.on('authentication_rejected', () => {
				this.setStatus(ConnectionStatus.AUTHENTICATION_REJECTED);
				console.error('[WS] Authentication rejected');
			});
			this.socketIoConnection.on('disconnect', (reason) => {
				console.warn(`[WS] Disconnected — ${reason}`);
				this.newDisconnection = true;
				this.setStatus(ConnectionStatus.CLOSED);
			});
			// Reconnection is the Manager's job, so its events fire on socket.io, not the socket.
			this.socketIoConnection.io.on('reconnect_attempt', (attemptNumber) => {
				console.log(`[WS] Reconnect attempt #${attemptNumber}`);
				this.handshakeChannels = setFromQuery(this.socketIoConnection.io.opts.query);
				this.setStatus(ConnectionStatus.CONNECTING);
			});

			this.socketIoConnection.on('message', (data) => {
				Dashboard.parseGeneralData(data);
			});
		}
		return this.socketIoConnection;
	}

	send(data, eventType = 'message') {
		if(!data) {
			console.error('No data was provided');
			return false;
		}
		if(this.socketIoConnection) {
			this.socketIoConnection.emit(eventType, data);
		}
		else {
			console.error('Was not connected to the socket');
		}
	}
}

SocketConnection.HTTP = 1;
SocketConnection.WEBSOCKET = 2;

const connection = new SocketConnection();
export default connection;